import { LoaderSpinner } from './LoaderSpinner';

/**
 * The application's loading state. There is one, and this is it.
 *
 * <h2>Why there is only one</h2>
 *
 * There were several, and the cost was three sessions of chasing flicker. Two
 * page-load screens handing over to each other produced a blank frame, then two
 * loaders in a row, then a crossfade, then a cold-boot screen between two steps of
 * one flow — four correct fixes for one cause, which was that there was more than
 * one of them.
 *
 * <p>So: no `variant`, no second placement, no per-screen wording. A component
 * that can look like two things eventually looks like two things, and nobody can
 * say which is right.
 *
 * <h2>It covers the viewport and blurs what is behind</h2>
 *
 * `fixed`, not `absolute`. It was `absolute` with each call site wrapping it in
 * its own positioned `div`, which is a component asking every caller to remember
 * something — and a caller that forgets gets a loader over a card with the rest of
 * the page still clickable.
 *
 * <p>Through frosted glass the context survives: somebody watching a form submit
 * can still see what they typed, and it is still obvious nothing can be pressed.
 * Blanking the screen instead throws away where they were, and the page returning
 * reads as a navigation rather than a result.
 *
 * <h2>Where the styling lives, and why not here</h2>
 *
 * In the `<style>` block in index.html, as `.xa-loader` and friends. Not
 * Tailwind utilities, and not a stylesheet of its own. Two reasons, both of them
 * about the same thing.
 *
 * <p><b>It has to appear before React does.</b> The measured hole on a cold start
 * is about 750ms of ground colour and nothing else, and React cannot render
 * before React exists. So index.html carries static markup for that first moment.
 * The instant there are two places describing how this looks, they drift -- which
 * is the whole history above, repeated one level down. One CSS definition, used
 * by the static markup and by this component, cannot drift.
 *
 * <p><b>A loader cannot wait for the stylesheet it is covering the wait for.</b>
 * index.css is 396 kB and in development arrives via JavaScript. Utilities from
 * it would leave the boot loader unstyled for exactly the period it exists to
 * cover.
 *
 * <p>A pleasant consequence: the glass themes stop being a special case. This used
 * to need `g-color` and `opacity-*` because `theme-dark-glass` redefines
 * `--background` but not `--foreground`, overrides colour with `!important`, and
 * left the subtitle mid-grey on dark glass. `--xa-ink` is keyed off `html.dark`,
 * which the theme script sets for every dark ground including the glass ones, so
 * the right colour is the only colour and nothing has to rescue it.
 *
 * <h2>The turning part is the Xtremand mark, and it is the whole loader</h2>
 *
 * Not a blue arc. For the ~750ms this covers on a cold start, nothing on screen
 * said whose application was starting; now the first thing painted is the product.
 *
 * <p>The mark itself does not turn -- a logo is illegible through most of a 360°
 * rotation and reads as broken rather than busy. A ring turns around it, and that
 * ring is the old arc. See {@link LoaderSpinner}, and `.xa-loader__spinner` in
 * `index.html` for how it is drawn.
 *
 * <p><b>There are no words under it, and so there is no `title` prop.</b>
 * "Please wait..." existed because a blue arc does not say what is being waited
 * for; the mark does, and a caption repeating it is a caption that says nothing.
 * A `subtitle` prop went with it -- no call site ever passed one, and the only
 * `title` anybody passed was "Loading...", which is the default reworded.
 *
 * <p>The text is not deleted, it is clipped. `role="status"` around an
 * aria-hidden graphic and nothing else is a live region with no content, which
 * announces nothing -- the wait would go silent for the people who cannot see the
 * mark. `.xa-loader__sr` keeps one word in the accessibility tree and out of the
 * picture.
 *
 * <p>There is no `.xa-loader__inner` wrapper either. Its one rule is
 * `text-align: center`, which centres {@link SectionLoader}'s label; with no
 * label to centre and `.xa-loader` already a centring flex container, it was an
 * element with nothing to do.
 */
export function AppLoader() {
  return (
    <div
      // `status`, not `alert`: this is a state, not something demanding the
      // reader leave what they are doing. `polite` announces it at the next
      // pause rather than interrupting mid-sentence.
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="xa-loader"
    >
      <LoaderSpinner />

      {/* Read aloud, never drawn. See `.xa-loader__sr` in index.html. */}
      <span className="xa-loader__sr">Loading</span>
    </div>
  );
}
